// components/Survey/SurveyBuilderPage.jsx — create / edit a survey: questions, settings and live preview
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button, Field, Input, Menu, MenuDivider, MenuGroup, MenuGroupHeader, MenuItem, MenuList, MenuPopover, MenuTrigger,
  Spinner, Switch, Text, Textarea, Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular, Save24Regular, Share24Regular, Eye24Regular, Settings24Regular, TaskListSquareLtr24Regular,
  Copy20Regular, Delete20Regular, ArrowUp20Regular, ArrowDown20Regular, ReOrderDotsVertical20Regular,
  Add20Regular, Dismiss20Regular, Branch20Regular, ErrorCircle20Regular, CheckmarkCircle20Regular, LockClosed24Regular,
  TextT24Regular, TextAlignLeft24Regular, RadioButton24Regular, CheckboxChecked24Regular, ChevronDown24Regular,
  ThumbLike24Regular, Star24Regular, SlideSize24Regular, Gauge24Regular, Table24Regular, NumberSymbol24Regular,
  Calendar24Regular, TextHeader124Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import { generateId } from "../../services/surveyService";
import {
  QUESTION_TYPES, TYPE_META, CHOICE_TYPES, SURVEY_TEMPLATES, AGREEMENT_SCALE, newQuestion, cloneQuestion,
  validateSurveyDefinition, countQuestions, estimateMinutes,
} from "./surveyUtils";
import { C, card, StatusChip, EmptyState, LoadingBlock, useToast } from "./surveyUI";
import SurveyRunner from "./SurveyRunner";
import ShareSurveyDialog from "./ShareSurveyDialog";

export const TYPE_ICONS = {
  short_text: TextT24Regular, long_text: TextAlignLeft24Regular, single_choice: RadioButton24Regular,
  multi_choice: CheckboxChecked24Regular, dropdown: ChevronDown24Regular, yes_no: ThumbLike24Regular,
  rating: Star24Regular, scale: SlideSize24Regular, nps: Gauge24Regular, likert: Table24Regular,
  number: NumberSymbol24Regular, date: Calendar24Regular, section: TextHeader124Regular,
};

const SCALE_PRESETS = {
  Agreement: AGREEMENT_SCALE,
  Satisfaction: ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"],
  Frequency: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  Quality: ["Very poor", "Poor", "Fair", "Good", "Excellent"],
};

const APP_HEADER_H = 60; // AppShell's sticky header

const EMPTY_SETTINGS ={ anonymous: false, closesAt: "", thankYouMessage: "", showProgress: true };

const fieldInput = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 14, fontFamily: "inherit",
  border: `1px solid ${C.line}`, borderRadius: 8, outline: "none", background: "#fff", color: C.ink,
};

// Switching type keeps text, required flag and — between choice types — the options
function convertQuestion(q, type) {
  const fresh = newQuestion(type);
  const keep = { id: q.id, title: q.title, description: q.description, required: type === "section" ? false : q.required, showIf: q.showIf };
  if (CHOICE_TYPES.includes(type) && q.options?.length) return { ...fresh, ...keep, options: q.options, allowOther: !!q.allowOther };
  return { ...fresh, ...keep, title: q.title || fresh.title };
}

// ── List editor for options / statements / scale points ────────────────────────
function ItemListEditor({ items, onChange, placeholder, bullet, addLabel = "Add option", min = 1 }) {
  const refs = useRef({});
  const update = (id, label) => onChange(items.map((o) => (o.id === id ? { ...o, label } : o)));
  const remove = (id) => onChange(items.filter((o) => o.id !== id));
  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  const add = (afterIndex = items.length - 1) => {
    const item = { id: generateId("o"), label: "" };
    const next = [...items];
    next.splice(afterIndex + 1, 0, item);
    onChange(next);
    setTimeout(() => refs.current[item.id]?.focus(), 30);
  };
  // Pasting several lines into one option splits them into separate options
  const onPaste = (e, i) => {
    const lines = e.clipboardData.getData("text").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return;
    e.preventDefault();
    const next = [...items];
    next.splice(i, 1, ...lines.map((label, k) => (k === 0 ? { ...items[i], label } : { id: generateId("o"), label })));
    onChange(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((o, i) => (
        <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 22, textAlign: "center", color: C.muted, fontSize: 12, flexShrink: 0 }}>{bullet(i)}</span>
          <input
            ref={(el) => { refs.current[o.id] = el; }}
            value={o.label}
            onChange={(e) => update(o.id, e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(i); } }}
            onPaste={(e) => onPaste(e, i)}
            placeholder={placeholder(i)}
            style={{ ...fieldInput, flex: 1, padding: "7px 10px", fontSize: 13 }}
          />
          <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} style={iconBtn(i === 0)}><ArrowUp20Regular /></button>
          <button type="button" aria-label="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)} style={iconBtn(i === items.length - 1)}><ArrowDown20Regular /></button>
          <button type="button" aria-label="Remove" disabled={items.length <= min} onClick={() => remove(o.id)} style={iconBtn(items.length <= min, true)}><Dismiss20Regular /></button>
        </div>
      ))}
      <button type="button" onClick={() => add()} style={{ alignSelf: "flex-start", marginLeft: 30, display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: C.brand, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "4px 0", fontFamily: "inherit" }}>
        <Add20Regular style={{ fontSize: 16 }} /> {addLabel}
      </button>
      <Text style={{ fontSize: 11, color: C.muted, marginLeft: 30 }}>Tip: press Enter for a new line, or paste a list to add many at once.</Text>
    </div>
  );
}

function iconBtn(disabled, danger) {
  return {
    width: 28, height: 28, borderRadius: 6, border: "none", background: "transparent", flexShrink: 0,
    color: disabled ? "#D2D0CE" : danger ? "#A19F9D" : "#8A8886", cursor: disabled ? "default" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
}

function MiniLabel({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "14px 0 8px" }}>{children}</div>;
}

// ── Type-specific settings ─────────────────────────────────────────────────────
function TypeSettings({ q, set }) {
  if (CHOICE_TYPES.includes(q.type)) {
    return (
      <>
        <MiniLabel>Options</MiniLabel>
        <ItemListEditor
          items={q.options || []}
          onChange={(options) => set({ options })}
          min={1}
          placeholder={(i) => `Option ${i + 1}`}
          bullet={(i) => (q.type === "multi_choice" ? "☐" : q.type === "dropdown" ? `${i + 1}.` : "○")}
        />
        <div style={{ marginTop: 10, marginLeft: 26 }}>
          <Switch checked={!!q.allowOther} onChange={(_, d) => set({ allowOther: d.checked })} label="Include an “Other (please specify)” option" />
        </div>
      </>
    );
  }
  if (q.type === "rating") {
    return (
      <>
        <MiniLabel>Number of stars</MiniLabel>
        <div style={{ display: "flex", gap: 6 }}>
          {[3, 5, 7, 10].map((n) => (
            <button key={n} type="button" onClick={() => set({ ratingMax: n })} style={pill(Number(q.ratingMax || 5) === n)}>{n}</button>
          ))}
        </div>
      </>
    );
  }
  if (q.type === "scale" || q.type === "nps") {
    return (
      <>
        {q.type === "scale" && (
          <>
            <MiniLabel>Scale range</MiniLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <select value={q.scaleMin} onChange={(e) => set({ scaleMin: Number(e.target.value) })} style={{ ...fieldInput, width: 80 }}>
                {[0, 1].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span style={{ color: C.muted, fontSize: 13 }}>to</span>
              <select value={q.scaleMax} onChange={(e) => set({ scaleMax: Number(e.target.value) })} style={{ ...fieldInput, width: 80 }}>
                {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </>
        )}
        <MiniLabel>End labels</MiniLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <input value={q.minLabel || ""} onChange={(e) => set({ minLabel: e.target.value })} placeholder={`Label for ${q.type === "nps" ? 0 : q.scaleMin}`} style={fieldInput} />
          <input value={q.maxLabel || ""} onChange={(e) => set({ maxLabel: e.target.value })} placeholder={`Label for ${q.type === "nps" ? 10 : q.scaleMax}`} style={fieldInput} />
        </div>
        {q.type === "nps" && <Text style={{ fontSize: 12, color: C.muted, display: "block", marginTop: 8 }}>Scored as Net Promoter: 9–10 promoters, 7–8 passives, 0–6 detractors.</Text>}
      </>
    );
  }
  if (q.type === "likert") {
    return (
      <>
        <MiniLabel>Statements (rows)</MiniLabel>
        <ItemListEditor items={q.rows || []} onChange={(rows) => set({ rows })} placeholder={(i) => `Statement ${i + 1}`} bullet={(i) => `${i + 1}.`} addLabel="Add statement" />
        <MiniLabel>Scale points (columns, lowest → highest)</MiniLabel>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {Object.entries(SCALE_PRESETS).map(([name, labels]) => (
            <button key={name} type="button" onClick={() => set({ columns: labels.map((label) => ({ id: generateId("o"), label })) })} style={pill(false)}>{name}</button>
          ))}
        </div>
        <ItemListEditor items={q.columns || []} onChange={(columns) => set({ columns })} min={2} placeholder={(i) => `Point ${i + 1}`} bullet={(i) => `${i + 1}`} addLabel="Add scale point" />
      </>
    );
  }
  if (q.type === "number") {
    return (
      <>
        <MiniLabel>Allowed range (optional)</MiniLabel>
        <div style={{ display: "flex", gap: 8, maxWidth: 320 }}>
          <input type="number" value={q.min ?? ""} onChange={(e) => set({ min: e.target.value })} placeholder="Min" style={fieldInput} />
          <input type="number" value={q.max ?? ""} onChange={(e) => set({ max: e.target.value })} placeholder="Max" style={fieldInput} />
        </div>
      </>
    );
  }
  return null;
}

function pill(on) {
  return {
    padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${on ? C.accent : C.line}`, background: on ? "#F0F6FF" : "#fff", color: on ? C.brand : C.ink2,
  };
}

// ── Skip logic editor ──────────────────────────────────────────────────────────
function LogicEditor({ q, questions, set }) {
  const index = questions.findIndex((x) => x.id === q.id);
  const sources = questions.slice(0, index).filter((x) => ["single_choice", "dropdown", "yes_no", "multi_choice"].includes(x.type));
  const cond = q.showIf || {};
  const src = sources.find((x) => x.id === cond.questionId);
  const answers = src ? (src.type === "yes_no" ? [{ id: "yes", label: "Yes" }, { id: "no", label: "No" }] : src.options || []) : [];
  if (!sources.length) {
    return <Text style={{ fontSize: 12, color: C.muted }}>Add a choice or Yes/No question above this one to use skip logic.</Text>;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, color: C.ink2 }}>
      Show only when
      <select value={cond.questionId || ""} onChange={(e) => set({ showIf: e.target.value ? { questionId: e.target.value, equals: "" } : undefined })} style={{ ...fieldInput, width: "auto", maxWidth: 280, fontSize: 13 }}>
        <option value="">— always show —</option>
        {sources.map((s) => <option key={s.id} value={s.id}>{s.title || "Untitled question"}</option>)}
      </select>
      {src && (
        <>
          {src.type === "multi_choice" ? "includes" : "is"}
          <select value={cond.equals || ""} onChange={(e) => set({ showIf: { ...cond, equals: e.target.value } })} style={{ ...fieldInput, width: "auto", maxWidth: 220, fontSize: 13 }}>
            <option value="">Select answer…</option>
            {answers.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </>
      )}
    </div>
  );
}

// ── Question card ──────────────────────────────────────────────────────────────
// Menu of question types, grouped; wraps whatever trigger it is given
function AddQuestionMenu({ onPick, children }) {
  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>{children}</MenuTrigger>
      <MenuPopover style={{ maxHeight: 420, overflowY: "auto" }}>
        <MenuList>
          {["Text", "Choice", "Scale", "Other"].map((g, gi) => (
            <React.Fragment key={g}>
              {gi > 0 && <MenuDivider />}
              <MenuGroup>
                <MenuGroupHeader>{g}</MenuGroupHeader>
                {QUESTION_TYPES.filter((t) => t.group === g).map((t) => {
                  const Icon = TYPE_ICONS[t.type];
                  return (
                    <MenuItem key={t.type} icon={<Icon style={{ color: C.brand }} />} secondaryContent={t.desc} onClick={() => onPick(t.type)}>
                      {t.label}
                    </MenuItem>
                  );
                })}
              </MenuGroup>
            </React.Fragment>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function QuestionCard({ q, number, active, questions, onActivate, onChange, onDuplicate, onAddBelow, onDelete, onMove, isFirst, isLast, dragProps, issue }) {
  const Icon = TYPE_ICONS[q.type];
  const set = (patch) => onChange({ ...q, ...patch });
  const [showLogic, setShowLogic] = useState(!!q.showIf?.questionId);
  const logicSource = q.showIf?.questionId && questions.find((x) => x.id === q.showIf.questionId);

  if (!active) {
    return (
      <div {...dragProps} onClick={onActivate} style={{ ...card, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, borderLeft: `4px solid ${issue ? C.danger : q.type === "section" ? C.navy2 : "transparent"}`, ...dragProps.style }}>
        <span style={{ color: "#C8C6C4", cursor: "grab", display: "flex" }} title="Drag to reorder"><ReOrderDotsVertical20Regular /></span>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: q.type === "section" ? "#E8EEF9" : "#F0F6FF", color: q.type === "section" ? C.navy2 : C.brand, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon style={{ fontSize: 18 }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: q.type === "section" ? 15 : 14, fontWeight: q.type === "section" ? 800 : 600, color: q.title ? C.ink : C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {q.type !== "section" && <span style={{ color: C.accent, marginRight: 6 }}>{number}.</span>}
            {q.title || (q.type === "section" ? "Untitled section" : "Untitled question")}
          </div>
          <div style={{ fontSize: 12, color: C.muted, display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
            <span>{TYPE_META[q.type].label}</span>
            {CHOICE_TYPES.includes(q.type) && <span>· {(q.options || []).length} options</span>}
            {q.type === "likert" && <span>· {(q.rows || []).length} statements</span>}
            {q.required && <span style={{ color: C.danger }}>· Required</span>}
            {logicSource && <span style={{ color: "#5B3DC4", display: "inline-flex", alignItems: "center", gap: 2 }}>· <Branch20Regular style={{ fontSize: 13 }} /> Conditional</span>}
            {issue && <span style={{ color: C.danger }}>· {issue}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...card, padding: 0, borderLeft: `4px solid ${C.accent}`, boxShadow: "0 8px 30px rgba(0,82,204,0.12)" }}>
      <div style={{ padding: "18px 20px 6px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <input
              autoFocus={!q.title}
              value={q.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder={q.type === "section" ? "Section heading" : "Type your question"}
              style={{ ...fieldInput, fontSize: q.type === "section" ? 18 : 16, fontWeight: q.type === "section" ? 800 : 600, padding: "10px 12px", background: C.soft, border: "1px solid transparent", borderBottom: `2px solid ${C.accent}`, borderRadius: "8px 8px 0 0" }}
            />
          </div>
          <select value={q.type} onChange={(e) => onChange(convertQuestion(q, e.target.value))} style={{ ...fieldInput, width: 200, padding: "10px 10px", cursor: "pointer" }} aria-label="Question type">
            {["Text", "Choice", "Scale", "Other"].map((g) => (
              <optgroup key={g} label={g}>
                {QUESTION_TYPES.filter((t) => t.group === g).map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <textarea
          value={q.description || ""}
          onChange={(e) => set({ description: e.target.value })}
          placeholder={q.type === "section" ? "Optional intro for this section" : "Description or help text (optional)"}
          rows={1}
          style={{ ...fieldInput, marginTop: 8, fontSize: 13, color: C.ink2, resize: "vertical", border: "1px dashed #E1DFDD" }}
        />
        <TypeSettings q={q} set={set} />
        {showLogic && q.type !== "section" && (
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#F6F3FF", border: "1px solid #E2DAFF", borderRadius: 8 }}>
            <LogicEditor q={q} questions={questions} set={set} />
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "10px 14px", borderTop: `1px solid ${C.line}`, marginTop: 14, flexWrap: "wrap" }}>
        <Tooltip content="Move up" relationship="label"><button type="button" disabled={isFirst} onClick={() => onMove(-1)} style={iconBtn(isFirst)}><ArrowUp20Regular /></button></Tooltip>
        <Tooltip content="Move down" relationship="label"><button type="button" disabled={isLast} onClick={() => onMove(1)} style={iconBtn(isLast)}><ArrowDown20Regular /></button></Tooltip>
        {q.type !== "section" && (
          <Tooltip content="Skip logic" relationship="label">
            <button type="button" onClick={() => { if (showLogic) set({ showIf: undefined }); setShowLogic(!showLogic); }} style={{ ...iconBtn(false), color: showLogic ? "#5B3DC4" : "#8A8886" }}><Branch20Regular /></button>
          </Tooltip>
        )}
        <div style={{ flex: 1 }} />
        <AddQuestionMenu onPick={onAddBelow}>
          <Button size="small" appearance="subtle" icon={<Add20Regular />} style={{ color: C.brand, fontWeight: 600 }}>Add question below</Button>
        </AddQuestionMenu>
        <Tooltip content="Duplicate" relationship="label"><button type="button" onClick={onDuplicate} style={iconBtn(false)}><Copy20Regular /></button></Tooltip>
        <Tooltip content="Delete" relationship="label"><button type="button" onClick={onDelete} style={iconBtn(false, true)}><Delete20Regular /></button></Tooltip>
        {q.type !== "section" && (
          <>
            <div style={{ width: 1, height: 22, background: C.line, margin: "0 8px" }} />
            <Switch checked={!!q.required} onChange={(_, d) => set({ required: d.checked })} label="Required" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function SurveyBuilderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserAccess();
  const { surveyById, responses, loading, saveSurvey } = useSurveys();
  const existing = id ? surveyById[id] : null;

  const [draft, setDraft] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [tab, setTab] = useState("questions");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [shareOpen, setShareOpen] = useState(!!location.state?.openShare);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [toast, showToast] = useToast();

  // Initialise once the survey (or template) is available
  useEffect(() => {
    if (draft || loading) return;
    if (id) {
      if (existing) setDraft({ ...existing, settings: { ...EMPTY_SETTINGS, ...existing.settings } });
      return;
    }
    const tpl = SURVEY_TEMPLATES.find((t) => t.key === params.get("template")) || SURVEY_TEMPLATES[0];
    const built = tpl.build();
    setDraft({ ...built, status: "draft", settings: { ...EMPTY_SETTINGS } });
    setActiveId(built.questions[0]?.id || null);
  }, [draft, loading, id, existing, params]);

  // Warn before leaving with unsaved edits
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const responseCount = useMemo(() => (id ? responses.filter((r) => r.surveyId === id).length : 0), [responses, id]);
  const problems = useMemo(() => (draft ? validateSurveyDefinition(draft) : []), [draft]);

  // Per-question issues shown on collapsed cards
  const questionIssues = useMemo(() => {
    if (!draft || !showIssues) return {};
    const out = {};
    draft.questions.forEach((q) => {
      if (q.type === "section") return;
      if (!q.title?.trim()) out[q.id] = "Needs question text";
      else if (CHOICE_TYPES.includes(q.type) && (q.options || []).filter((o) => o.label.trim()).length < 2) out[q.id] = "Needs 2+ options";
    });
    return out;
  }, [draft, showIssues]);

  if (!isAdmin) return <EmptyState icon={LockClosed24Regular} title="Access restricted" text="Only administrators can build surveys." />;
  if (loading || (!draft && (!id || existing))) return <LoadingBlock label="Loading survey…" />;
  if (!draft) return <EmptyState icon={TaskListSquareLtr24Regular} title="Survey not found" text="It may have been deleted." action={<Button onClick={() => navigate("/surveys")}>Back to surveys</Button>} />;

  const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
  const updateSettings = (patch) => update({ settings: { ...draft.settings, ...patch } });
  const setQuestions = (questions) => update({ questions });

  // afterId: insert below that question; null → end of survey; undefined → below the active one
  const insertQuestion = (type, afterId) => {
    const q = newQuestion(type);
    const idx = afterId === null ? -1 : draft.questions.findIndex((x) => x.id === (afterId ?? activeId));
    const next = [...draft.questions];
    next.splice(idx === -1 ? next.length : idx + 1, 0, q);
    setQuestions(next);
    setActiveId(q.id);
    setTab("questions");
    setTimeout(() => document.getElementById(`bq-${q.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const changeQuestion = (q) => setQuestions(draft.questions.map((x) => (x.id === q.id ? q : x)));
  const duplicateQuestion = (q) => {
    const copy = cloneQuestion(q);
    const idx = draft.questions.findIndex((x) => x.id === q.id);
    const next = [...draft.questions];
    next.splice(idx + 1, 0, copy);
    setQuestions(next);
    setActiveId(copy.id);
  };
  const deleteQuestion = (q) => {
    const idx = draft.questions.findIndex((x) => x.id === q.id);
    // Drop skip-logic that pointed at the removed question
    const next = draft.questions.filter((x) => x.id !== q.id).map((x) => (x.showIf?.questionId === q.id ? { ...x, showIf: undefined } : x));
    setQuestions(next);
    setActiveId(next[Math.max(0, idx - 1)]?.id || null);
  };
  const moveQuestion = (q, d) => {
    const idx = draft.questions.findIndex((x) => x.id === q.id);
    const next = [...draft.questions];
    [next[idx], next[idx + d]] = [next[idx + d], next[idx]];
    setQuestions(next);
  };
  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const next = [...draft.questions];
    const from = next.findIndex((x) => x.id === dragId);
    const [item] = next.splice(from, 1);
    next.splice(next.findIndex((x) => x.id === targetId), 0, item);
    setQuestions(next);
  };

  const save = async ({ thenShare = false, quiet = false } = {}) => {
    if (thenShare && problems.length) {
      setShowIssues(true);
      showToast(problems[0], "error");
      return null;
    }
    setSaving(true);
    try {
      // Blank options / statements are dropped rather than shown to staff
      const clean = (items) => (items ? items.filter((o) => o.label.trim()) : items);
      const questions = draft.questions.map((q) => ({ ...q, options: clean(q.options), rows: clean(q.rows), columns: clean(q.columns) }));
      const saved = await saveSurvey({ ...draft, questions, title: draft.title.trim() || "Untitled survey" });
      setDraft((d) => ({ ...d, questions, id: saved.id, _spItemId: saved._spItemId, status: saved.status }));
      setDirty(false);
      if (!id) navigate(`/surveys/${saved.id}/edit`, { replace: true, state: thenShare ? { openShare: true } : undefined });
      if (!quiet) showToast(thenShare ? "Saved — choose who to share with" : "Survey saved");
      if (thenShare) setShareOpen(true);
      return saved;
    } catch (err) {
      showToast(err.message, "error");
      return null;
    } finally {
      setSaving(false);
    }
  };

  let qNumber = 0;
  const numbers = {};
  draft.questions.forEach((q) => { if (q.type !== "section") numbers[q.id] = ++qNumber; });

  const TABS = [
    { key: "questions", label: "Questions", Icon: TaskListSquareLtr24Regular },
    { key: "settings", label: "Settings", Icon: Settings24Regular },
    { key: "preview", label: "Preview", Icon: Eye24Regular },
  ];

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      {/* Top bar — in normal flow (not sticky) so it never covers the content below */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...card, padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", boxShadow: "0 2px 10px rgba(0,26,61,0.08)" }}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => { if (!dirty || window.confirm("Discard unsaved changes?")) navigate("/surveys"); }} aria-label="Back" />
          <div style={{ minWidth: 180, flex: "1 1 220px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <Text style={{ fontWeight: 700, fontSize: 15, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{draft.title || "Untitled survey"}</Text>
              <StatusChip status={draft.status || "draft"} />
            </div>
            <Text style={{ fontSize: 12, color: dirty ? "#B8430A" : C.muted, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dirty ? "Unsaved changes" : draft.id ? "All changes saved" : "Not saved yet"} · {countQuestions(draft.questions)} questions · ~{estimateMinutes(draft.questions)} min
            </Text>
          </div>
          <div style={{ display: "flex", gap: 2, background: C.page, borderRadius: 10, padding: 3 }}>
            {TABS.map(({ key, label, Icon }) => (
              <button key={key} type="button" onClick={() => setTab(key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", cursor: "pointer", fontFamily: "inherit", padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: tab === key ? 700 : 500, background: tab === key ? "#fff" : "transparent", color: tab === key ? C.brand : C.ink2, boxShadow: tab === key ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>
                <Icon style={{ fontSize: 16 }} /> {label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button icon={saving ? <Spinner size="tiny" /> : <Save24Regular />} disabled={saving} onClick={() => save()}>Save</Button>
            <Button appearance="primary" icon={<Share24Regular />} disabled={saving || draft.status === "closed"} onClick={() => save({ thenShare: true, quiet: true })}>Save & share</Button>
          </div>
        </div>
      </div>

      {responseCount > 0 && (
        <div style={{ background: "#FFF8E1", border: "1px solid #FFE08A", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#6B4E00", marginBottom: 14, lineHeight: 1.6 }}>
          This survey already has <strong>{responseCount} response{responseCount === 1 ? "" : "s"}</strong>. Editing wording is safe; deleting questions or options hides their past answers from analysis, and changing a question's type may make earlier answers unreadable.
        </div>
      )}

      {tab === "preview" ? (
        <SurveyRunner survey={draft} mode="preview" onSubmit={async () => {}} />
      ) : tab === "settings" ? (
        <div style={{ ...card, padding: "22px 24px", maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <Field label="Category" hint="Groups surveys in lists and reports, e.g. Engagement, HSE, Training">
            <Input value={draft.category || ""} onChange={(_, d) => update({ category: d.value })} placeholder="e.g. Engagement" />
          </Field>
          <div>
            <Switch checked={!!draft.settings.anonymous} onChange={(_, d) => updateSettings({ anonymous: d.checked })} label="Anonymous responses" />
            <Text style={{ fontSize: 12, color: C.muted, display: "block", marginLeft: 44, lineHeight: 1.6 }}>
              Names are hidden in results and exports. Participation (who has responded) is still tracked so each person answers once per share.
            </Text>
          </div>
          <div>
            <Switch checked={draft.settings.showProgress !== false} onChange={(_, d) => updateSettings({ showProgress: d.checked })} label="Show a progress bar to respondents" />
          </div>
          <Field label="Close date (optional)" hint="After this date staff can no longer submit. You can also close the survey manually at any time.">
            <Input type="date" value={draft.settings.closesAt || ""} onChange={(_, d) => updateSettings({ closesAt: d.value })} style={{ maxWidth: 220 }} />
          </Field>
          <Field label="Thank-you message" hint="Shown after a staff member submits">
            <Textarea value={draft.settings.thankYouMessage || ""} onChange={(_, d) => updateSettings({ thankYouMessage: d.value })} rows={3} placeholder="Your response has been recorded. We appreciate you taking the time to share your feedback." />
          </Field>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 260px", gap: 18, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Title card */}
            <div style={{ ...card, overflow: "hidden" }}>
              <div style={{ height: 8, background: `linear-gradient(90deg, ${C.navy2}, ${C.accent}, #00A3C4)` }} />
              <div style={{ padding: "18px 22px" }}>
                <input value={draft.title} onChange={(e) => update({ title: e.target.value })} placeholder="Survey title" style={{ ...fieldInput, fontSize: 24, fontWeight: 800, color: C.navy, border: "none", borderBottom: `2px solid ${showIssues && !draft.title.trim() ? C.danger : C.line}`, borderRadius: 0, padding: "6px 2px" }} />
                <textarea value={draft.description} onChange={(e) => update({ description: e.target.value })} placeholder="Describe the purpose of this survey and how the results will be used. Respondents see this first." rows={2} style={{ ...fieldInput, marginTop: 10, border: "none", borderBottom: `1px solid ${C.line}`, borderRadius: 0, padding: "6px 2px", color: C.ink2, resize: "vertical" }} />
              </div>
            </div>

            {draft.questions.length === 0 && (
              <EmptyState icon={TaskListSquareLtr24Regular} title="No questions yet" text="Add your first question to get started." />
            )}

            {draft.questions.map((q, i) => (
              <div key={q.id} id={`bq-${q.id}`} style={{ outline: overId === q.id && dragId !== q.id ? `2px dashed ${C.accent}` : "none", borderRadius: 12 }}>
                <QuestionCard
                  q={q}
                  number={numbers[q.id]}
                  active={activeId === q.id}
                  questions={draft.questions}
                  issue={questionIssues[q.id]}
                  onActivate={() => setActiveId(q.id)}
                  onChange={changeQuestion}
                  onDuplicate={() => duplicateQuestion(q)}
                  onAddBelow={(type) => insertQuestion(type, q.id)}
                  onDelete={() => deleteQuestion(q)}
                  onMove={(d) => moveQuestion(q, d)}
                  isFirst={i === 0}
                  isLast={i === draft.questions.length - 1}
                  dragProps={{
                    draggable: true,
                    onDragStart: () => setDragId(q.id),
                    onDragOver: (e) => { e.preventDefault(); setOverId(q.id); },
                    onDragLeave: () => setOverId(null),
                    onDrop: () => { dropOn(q.id); setDragId(null); setOverId(null); },
                    onDragEnd: () => { setDragId(null); setOverId(null); },
                    style: { opacity: dragId === q.id ? 0.4 : 1 },
                  }}
                />
              </div>
            ))}

            <AddQuestionMenu onPick={(type) => insertQuestion(type, null)}>
              <button
                type="button"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "16px", borderRadius: 12, border: `2px dashed #C7DBFF`, background: "#F7FAFF", color: C.brand, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#EEF4FF"; e.currentTarget.style.borderColor = C.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#F7FAFF"; e.currentTarget.style.borderColor = "#C7DBFF"; }}
              >
                <Add20Regular /> Add question
              </button>
            </AddQuestionMenu>
          </div>

          {/* Side panel */}
          <div style={{ position: "sticky", top: APP_HEADER_H + 16, display: "flex", flexDirection: "column", gap: 12, maxHeight: `calc(100vh - ${APP_HEADER_H + 32}px)`, overflowY: "auto" }}>
            <div style={{ ...card, padding: "14px 14px 10px" }}>
              <Text style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 8 }}>Add question</Text>
              {["Text", "Choice", "Scale", "Other"].map((g) => (
                <div key={g} style={{ marginBottom: 6 }}>
                  {QUESTION_TYPES.filter((t) => t.group === g).map((t) => {
                    const Icon = TYPE_ICONS[t.type];
                    return (
                      <Tooltip key={t.type} content={t.desc} relationship="description" positioning="before">
                        <button
                          type="button"
                          onClick={() => insertQuestion(t.type)}
                          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "7px 8px", border: "none", background: "transparent", borderRadius: 8, cursor: "pointer", fontSize: 13, color: C.ink, fontFamily: "inherit", textAlign: "left" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#F0F6FF"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <Icon style={{ fontSize: 18, color: C.brand }} /> {t.label}
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>

            <div style={{ ...card, padding: "14px 16px" }}>
              <Text style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 8 }}>Ready to share?</Text>
              {problems.length === 0 ? (
                <div style={{ display: "flex", gap: 6, fontSize: 13, color: "#0B6A0B" }}><CheckmarkCircle20Regular style={{ fontSize: 16, flexShrink: 0 }} /> Looks good</div>
              ) : problems.slice(0, 5).map((p) => (
                <div key={p} style={{ display: "flex", gap: 6, fontSize: 12, color: C.danger, marginBottom: 4, lineHeight: 1.5 }}><ErrorCircle20Regular style={{ fontSize: 14, flexShrink: 0, marginTop: 2 }} /> {p}</div>
              ))}
              {problems.length > 5 && <Text style={{ fontSize: 12, color: C.muted }}>+{problems.length - 5} more</Text>}
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                <Button size="small" icon={saving ? <Spinner size="tiny" /> : <Save24Regular />} disabled={saving} onClick={() => save()} style={{ flex: 1 }}>Save</Button>
                <Button size="small" appearance="primary" icon={<Share24Regular />} disabled={saving || draft.status === "closed"} onClick={() => save({ thenShare: true, quiet: true })} style={{ flex: 1 }}>Share</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {shareOpen && draft.id && (
        <ShareSurveyDialog surveyId={draft.id} onClose={() => setShareOpen(false)} onDone={(msg, type) => { showToast(msg, type); if (type !== "error") navigate(`/surveys/${draft.id}/results?tab=participation`); }} />
      )}
      {toast}
    </div>
  );
}
